import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ObjectTarget } from "../../../app/permissions";
import { MessagePriority } from "../messagePriority";
import { RootState } from "../../../app/store";
import { error, failedResponse, getBackendUrl, loaded, loading, LoadState, LoadStateInfo, notStarted } from "../../common";

export interface MassiveMessage {
	id: number,
	schoolId: number,
	classroomId: number,
	userId: number,
	processed: string,
	messagePriority: MessagePriority,
    target: ObjectTarget,
	targetId: number,
}

export interface MassiveMessageRequest {
    schoolId?: number;
}

export const retrieveMassiveMessages = createAsyncThunk(
    "retrieveMassiveMessages",
    async (param: MassiveMessageRequest, thunkAPI) => {
        console.debug("retrieve massive messages");

        const token = (thunkAPI.getState() as RootState).user.token;

        const response = await fetch(`${getBackendUrl()}/api/conversation/massive/message/${param.schoolId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${token}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param, { getState }) => {
            const state = getState() as RootState;
            if(! state.user.token) {
                console.debug("No token, not loading massive message list");
                return false;
            }
            if (state.massiveMessage.state.state === LoadState.NotStarted) {
                // Only load if never loaded before
                return true; 
            }
            return false;
        },
    }
);

export interface MassiveMessageState {
    state: LoadStateInfo;
    request?: MassiveMessageRequest;
    list?: MassiveMessage[];
}

const initialState: MassiveMessageState = {
    state: notStarted(),
};

export const massiveMessageSlice = createSlice({
    name: "massiveMessage",
    initialState,
    reducers: {
        clean: (state) => {
            state.state = notStarted();
            state.request = undefined;
            state.list = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveMassiveMessages.pending, (state, action) => {
                state.state = loading();
                state.request = action.meta.arg;
                state.list = undefined;
            })
            .addCase(retrieveMassiveMessages.fulfilled, (state, action) => {
                state.state = loaded();
                state.list = action.payload;
            })
            .addCase(retrieveMassiveMessages.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

// export const { } = conversationListSlice.actions;

export const selectMassiveMessage = (state: RootState) => state.massiveMessage;

export default massiveMessageSlice.reducer;
